import React, { useEffect, useState } from 'react';
import { Plus, Loader2, Mail, MailOpen, ArrowDown, ArrowUp } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useLang } from '@/context/LangContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import DatePicker from '@/components/DatePicker';

export default function Courriers() {
    const { t } = useLang();
    const { user, loading: authLoading } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [rows, setRows] = useState([]);
    const [projects, setProjects] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        direction: 'inbound', subject: '', body: '', reference: '',
        from_party: '', to_party: '', project_id: '', received_at: '',
    });

    const load = async () => {
        if (!currentWorkspace) return;
        const wid = currentWorkspace.id;
        const [{ data: c }, { data: pr }] = await Promise.all([
            supabase.from('correspondence').select('*,projects:project_id(name)').eq('workspace_id', wid).order('created_at', { ascending: false }),
            supabase.from('projects').select('id,name').eq('workspace_id', wid).is('deleted_at', null).order('name'),
        ]);
        setRows(c || []); setProjects(pr || []);
    };
    useEffect(() => { if (!authLoading && user) load(); /* eslint-disable-next-line */ }, [authLoading, user, currentWorkspace]);

    const submit = async (e) => {
        e.preventDefault();
        const payload = {
            workspace_id: currentWorkspace.id,
            direction: form.direction,
            subject: form.subject,
            body: form.body || null,
            reference: form.reference || null,
            from_party: form.from_party || null,
            to_party: form.to_party || null,
            project_id: form.project_id || null,
            received_at: form.received_at || null,
            created_by: user.id,
        };
        const { error } = await supabase.from('correspondence').insert(payload);
        if (!error) {
            setOpen(false);
            setForm({ direction: 'inbound', subject: '', body: '', reference: '', from_party: '', to_party: '', project_id: '', received_at: '' });
            load();
        }
    };

    if (!currentWorkspace) return <div className="text-center py-20 text-foreground/60">{t('common.no_workspace')}</div>;

    return (
        <div className="space-y-8">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow mb-2">{t('courriers.eyebrow')}</div>
                    <h1 className="font-heading text-4xl">{t('courriers.title')}</h1>
                    <p className="text-foreground/70 mt-3 max-w-3xl">{t('courriers.sub')}</p>
                </div>
                <Button onClick={() => setOpen(true)} className="bg-primary hover:bg-primary-hover" data-testid="courriers-new-btn"><Plus size={14} className="mr-1.5" /> {t('courriers.new')}</Button>
            </div>

            <div className="card-soft overflow-hidden">
                <table className="w-full">
                    <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-6 py-3">{t('courriers.col_direction')}</th>
                            <th className="px-6 py-3">{t('courriers.col_subject')}</th>
                            <th className="px-6 py-3">{t('courriers.col_party')}</th>
                            <th className="px-6 py-3">{t('courriers.col_project')}</th>
                            <th className="px-6 py-3">{t('courriers.col_date')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {rows.length === 0 && <tr><td colSpan={5} className="px-6 py-10 text-center text-foreground/60" data-testid="courriers-empty">{t('courriers.empty')}</td></tr>}
                        {rows.map(c => (
                            <tr key={c.id} data-testid={`courrier-row-${c.id}`}>
                                <td className="px-6 py-3">
                                    <span className={`phase-badge ${c.direction === 'inbound' ? 'bg-primary-light text-primary' : 'bg-secondary-light text-secondary'} inline-flex items-center`}>
                                        {c.direction === 'inbound' ? <ArrowDown size={10} className="mr-1" /> : <ArrowUp size={10} className="mr-1" />}
                                        {c.direction === 'inbound' ? t('courriers.inbound') : t('courriers.outbound')}
                                    </span>
                                </td>
                                <td className="px-6 py-3">
                                    <div className="font-medium">{c.subject}</div>
                                    {c.reference && <div className="text-xs font-mono text-muted-foreground">{c.reference}</div>}
                                </td>
                                <td className="px-6 py-3 text-sm">{c.direction === 'inbound' ? c.from_party : c.to_party}</td>
                                <td className="px-6 py-3 text-foreground/80">{c.projects?.name || '—'}</td>
                                <td className="px-6 py-3 text-sm">{c.received_at || new Date(c.created_at).toLocaleDateString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-xl">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{t('courriers.new')}</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-4" data-testid="courrier-form">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>{t('courriers.col_direction')}</Label>
                                <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="courrier-direction">
                                    <option value="inbound">{t('courriers.inbound')}</option>
                                    <option value="outbound">{t('courriers.outbound')}</option>
                                </select>
                            </div>
                            <div className="space-y-2"><Label>{t('courriers.col_ref')}</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="CR-2026-001" data-testid="courrier-ref" /></div>
                        </div>
                        <div className="space-y-2"><Label>{t('courriers.col_subject')}</Label><Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="courrier-subject" /></div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>{t('courriers.from')}</Label><Input value={form.from_party} onChange={(e) => setForm({ ...form, from_party: e.target.value })} data-testid="courrier-from" /></div>
                            <div className="space-y-2"><Label>{t('courriers.to')}</Label><Input value={form.to_party} onChange={(e) => setForm({ ...form, to_party: e.target.value })} data-testid="courrier-to" /></div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>{t('grants.col_project')}</Label>
                                <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="courrier-project">
                                    <option value="">—</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                            <div className="space-y-2"><Label>{t('courriers.col_date')}</Label><DatePicker value={form.received_at} onChange={(v) => setForm({ ...form, received_at: v })} testId="courrier-date" /></div>
                        </div>
                        <div className="space-y-2"><Label>{t('courriers.body')}</Label><Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="courrier-body" /></div>
                        <DialogFooter><Button type="button" variant="ghost" onClick={() => setOpen(false)}>{t('common.cancel')}</Button><Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="courrier-submit">{t('common.save')}</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
